import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { stopViewAs } from "../utils/viewAs";

const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;

/**
 * Knop links van de profiel-avatar zolang een beheerder de applicatie bekijkt met de rechten van een andere
 * gebruiker. De popup legt uit wat dat betekent en laat stoppen.
 */
export default function ViewAsMenu() {
  const { viewAs } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!viewAs) return null;

  const name = fullName(viewAs);
  const stop = () => {
    setOpen(false);
    stopViewAs();
    navigate(`/users/${viewAs.user_id}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={`Je bekijkt de applicatie als ${name}`}
        className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z" />
        </svg>
        <span className="hidden max-w-[10rem] truncate sm:inline">Als {name}</span>
      </button>

      {open && (
        <div role="dialog" aria-label="Bekijk als" className="absolute right-0 z-50 mt-2 w-80 rounded-md bg-white p-4 text-sm text-gray-700 shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="mb-2 font-semibold text-gray-900">Je bekijkt de applicatie als {name}</div>
          <p className="mb-2">
            Menu en schermen tonen precies wat deze gebruiker te zien krijgt, op basis van {viewAs.permissions?.length || 0} rechten.
          </p>
          {viewAs.roles?.length > 0 && (
            <p className="mb-2 text-xs text-gray-500">Rollen: {viewAs.roles.map((r) => r.name).join(", ")}</p>
          )}
          <p className="mb-3 text-xs text-gray-500">
            Je blijft zelf ingelogd: je ziet en wijzigt je eigen gegevens, niet die van {name}. Deze keuze geldt enkel in dit tabblad.
          </p>
          <div className="flex items-center justify-between gap-2">
            <Link to={`/users/${viewAs.user_id}`} onClick={() => setOpen(false)} className="text-xs text-blue-600 hover:underline">
              Naar de gebruikerspagina
            </Link>
            <button type="button" onClick={stop} className="rounded bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600">
              Stoppen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
